import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { Phone, MoreVertical, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { MessageBubble } from './MessageBubble'
import { MessageInput } from './MessageInput'
import { ChatSyncBadge } from './ChatSyncBadge'
import { Avatar } from '@/components/common/Avatar'
import { Badge } from '@/components/common/Badge'
import { Spinner } from '@/components/common/Spinner'
import { useMessagesStore } from '@/stores/messagesStore'
import { CONVERSATION_STATUS } from '@/utils/app-constants'
import { formatInternationalLeadNumber } from '@/utils/formatters'
import ChatSyncService, { type ConversationSyncStatus } from '@/services/chat-sync.service'
import type { Conversation } from '@/types'
import type { WebSocketHookReturn } from '@/types/websocket'

interface ChatWindowProps {
  conversation: Conversation
  ws: WebSocketHookReturn
  onClose?: () => void
  onFinalize?: () => Promise<void>
  onOpenContactDetails?: () => void
  canFinalize?: boolean
  isFinalizing?: boolean
  aiEnabled?: boolean | null
  syncStatus?: ConversationSyncStatus
  isReconciling?: boolean
}

const statusToBadge = {
  open: 'success',
  closed: 'default',
  waiting: 'warning',
} as const

export function ChatWindow({
  conversation,
  ws,
  onClose,
  onFinalize,
  onOpenContactDetails,
  canFinalize = false,
  isFinalizing = false,
  aiEnabled = null,
  syncStatus,
  isReconciling = false,
}: ChatWindowProps) {
  const contact = conversation.contact
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messages = useMessagesStore((s) => s.messages)
  const isLoadingMessages = useMessagesStore((s) => s.isLoadingMessages)
  const typingJids = useMessagesStore((s) => s.typingJids)
  const markMessageRendered = useMessagesStore((s) => s.markMessageRendered)
  const ackedMessagesRef = useRef(new Set<string>())

  const isContactTyping =
    contact?.jid && typingJids.has(contact.jid)

  const statusInfo = CONVERSATION_STATUS[conversation.status]

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  useEffect(() => {
    let cancelled = false

    async function acknowledgeMessages() {
      for (const message of messages) {
        if (message.conversation_id !== conversation.id) continue
        if (message.direction !== 'inbound') continue
        if (message.sync_status === 'rendered') continue
        if (ackedMessagesRef.current.has(message.id)) continue

        ackedMessagesRef.current.add(message.id)
        const now = new Date().toISOString()
        markMessageRendered(message.id, now, now)

        try {
          await ChatSyncService.ackMessage(message.id, conversation.id)
        } catch {
          if (!cancelled) {
            ackedMessagesRef.current.delete(message.id)
          }
        }
      }
    }

    acknowledgeMessages().catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [conversation.id, markMessageRendered, messages])

  const handleSend = async (content: string) => {
    if (!contact?.jid) return
    try {
      await ws.sendMessage(contact.jid, content, conversation.instance_id)
    } catch {
      toast.error('Erro ao enviar mensagem. Tente novamente.')
    }
  }

  const handleTyping = (isTyping: boolean) => {
    if (!contact?.jid) return
    ws.setTyping(conversation.instance_id, contact.jid, isTyping)
  }

  const name =
    contact?.name ||
    formatInternationalLeadNumber(contact?.phone_number ?? contact?.jid ?? '') ||
    'Desconhecido'
  const leadNumber = formatInternationalLeadNumber(contact?.phone_number ?? contact?.jid ?? '')

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col bg-[radial-gradient(circle_at_top_right,rgba(var(--color-brand-rgb),0.16),transparent_24%),radial-gradient(circle_at_bottom_right,rgba(var(--color-secondary-rgb),0.28),transparent_40%),var(--bg-secondary)]">
      {/* Header */}
      <div
        className={cn(
          'flex items-center gap-3 px-5 py-4',
          'border-b border-[var(--border-subtle)]',
          'glass-header shrink-0',
        )}
      >
        {onClose && (
          <button
            onClick={onClose}
            className="mr-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors duration-150 md:hidden"
            aria-label="Fechar chat"
          >
            <X size={18} />
          </button>
        )}

        <Avatar name={name} src={contact?.avatar_url} size="md" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onOpenContactDetails}
              className="truncate text-left font-semibold text-sm text-[var(--text-primary)] transition hover:text-[var(--color-brand)]"
            >
              {name}
            </button>
            <Badge
              variant={statusToBadge[conversation.status]}
              size="sm"
              dot
            >
              {statusInfo.label}
            </Badge>
            {aiEnabled !== null && (
              <Badge variant={aiEnabled ? 'success' : 'warning'} size="sm">
                {aiEnabled ? 'IA ativa' : 'IA pausada'}
              </Badge>
            )}
            {syncStatus && (
              <ChatSyncBadge
                status={syncStatus.syncStatus}
                maxLagMs={syncStatus.maxLagMs}
                isReconciling={isReconciling}
              />
            )}
          </div>
          {isContactTyping ? (
            <div className="flex items-center gap-1">
              <span className="text-xs text-[var(--color-brand)]">
                digitando
              </span>
              <div className="flex gap-0.5">
                <span className="typing-dot" style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'var(--color-brand)', display: 'inline-block', animation: 'typing-bounce 1.2s ease-in-out infinite' }} />
                <span className="typing-dot" style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'var(--color-brand)', display: 'inline-block', animation: 'typing-bounce 1.2s ease-in-out 0.2s infinite' }} />
                <span className="typing-dot" style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'var(--color-brand)', display: 'inline-block', animation: 'typing-bounce 1.2s ease-in-out 0.4s infinite' }} />
              </div>
            </div>
          ) : (
            leadNumber && (
              <p className="text-xs text-[var(--text-muted)] truncate">
                {leadNumber}
              </p>
            )
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            className={cn(
              'w-8 h-8 rounded-[var(--radius-sm)] flex items-center justify-center',
              'text-[var(--text-muted)] hover:text-[var(--text-primary)]',
              'hover:bg-[var(--glass-medium)] transition-colors duration-150',
            )}
            aria-label="Ligar"
          >
            <Phone size={16} />
          </button>
          {canFinalize && conversation.status !== 'closed' && (
            <button
              onClick={() => onFinalize?.().catch(() => undefined)}
              disabled={isFinalizing}
              className={cn(
                'h-8 rounded-[var(--radius-sm)] px-3 text-xs font-semibold',
                'border border-[var(--border-medium)] text-[var(--text-primary)]',
                'hover:bg-[var(--glass-medium)] transition-colors duration-150',
                'disabled:cursor-not-allowed disabled:opacity-60',
              )}
              aria-label="Finalizar conversa"
            >
              {isFinalizing ? 'Finalizando...' : 'Finalizar conversa'}
            </button>
          )}
          <button
            className={cn(
              'w-8 h-8 rounded-[var(--radius-sm)] flex items-center justify-center',
              'text-[var(--text-muted)] hover:text-[var(--text-primary)]',
              'hover:bg-[var(--glass-medium)] transition-colors duration-150',
            )}
            aria-label="Mais opções"
          >
            <MoreVertical size={16} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-5">
        {isLoadingMessages ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="md" label="Carregando mensagens..." />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-[var(--text-muted)]">
              Nenhuma mensagem ainda
            </p>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Inicie a conversa enviando uma mensagem
            </p>
          </div>
        ) : (
          <div className="flex w-full flex-col items-stretch gap-1">
            {messages.map((message) => (
              <MessageBubble key={message.message_id_wa ?? message.id} message={message} />
            ))}

            {/* Typing indicator */}
            {isContactTyping && (
              <div className="flex w-full justify-start">
                <div className="bubble-inbound px-3.5 py-2.5">
                  <div className="typing-indicator">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-[var(--border-subtle)] bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(12,14,40,0.72))] px-4 py-3 backdrop-blur-md">
        <div className="w-full">
          <MessageInput
            onSend={handleSend}
            onTyping={handleTyping}
            disabled={conversation.status === 'closed'}
            placeholder={
              conversation.status === 'closed'
                ? 'Conversa encerrada'
                : 'Digite uma mensagem...'
            }
          />
        </div>
      </div>
    </div>
  )
}
